import Add20 from '@carbon/icons-react/es/add/20';
import Bullhorn24 from '@carbon/icons-react/es/bullhorn/24';
import Close16 from '@carbon/icons-react/es/close/16';
import { Col, Row, Tooltip, Typography } from 'antd';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { StatusUpdatePrivacy } from 'api/status-updates';
import {
  TemplateCard,
  TemplateCardIconWrapper,
} from 'components/atoms/card/template-card';
import { OzoneDrawer } from 'components/atoms/drawer';
import { IconShell } from 'components/atoms/icon-shell';
import { PageLoading } from 'components/atoms/loading';
import { HelperText01, ProductiveHeading05, Text03 } from 'components/styles';
import { Template, TemplateObjectType } from 'data/templates/api';
import { useTranslation } from 'hooks/use-translation';
import { useGetAllStatusUpdateTemplatesQuery } from 'queries/templates/status-update-templates';
import { StatusUpdatesTemplateEmptyState } from './status-update-empty-state';

const Bullhorn = styled(Bullhorn24)`
  fill: var(--blue-40);
`;

const { Paragraph } = Typography;

const CreateFromScratchCard = styled(TemplateCard)`
  background-color: var(--blue-60);
`;

const StyledTemplateCardIconWrapper = styled(TemplateCardIconWrapper)`
  background-color: var(--blue-60);
  color: white;
`;

const StyledParagraph = styled(Paragraph)`
  color: white;
`;

const Title = styled.h5`
  ${ProductiveHeading05};
  padding-left: var(--spacing-03);
`;

const Help = styled.div`
  ${HelperText01};
  ${Text03};
  margin-top: var(--spacing-03);
  color: var(--white-70);
`;

export const StatusTemplatesList: FC<{
  visible: boolean;
  onClose(): void;
  onChooseTemplate(template: Template, type: StatusUpdatePrivacy): void;
  createUpdate(privacy: StatusUpdatePrivacy): void;
  isPrivate: boolean;
}> = ({ visible, onClose, onChooseTemplate, createUpdate, isPrivate }) => {
  const { data: templates = [], isLoading: loading } =
    useGetAllStatusUpdateTemplatesQuery();
  const templatePrivacy = isPrivate
    ? StatusUpdatePrivacy.Private
    : StatusUpdatePrivacy.Shared;

  const createFromScratch = useTranslation('su.start.from.scratch');
  const blankTemplate = useTranslation('su.blank.template');
  const blankPrivateTemplate = useTranslation('su.blank.private.template');

  const newUpdate = useTranslation('templates.status_updates.new');

  const newPrivateUpdate = useTranslation(
    'templates.status_updates.new.private',
  );

  const privateTemplates = useMemo(
    () => templates.filter((template) => template.private),
    [templates],
  );

  const sharedTemplates = useMemo(
    () => templates.filter((template) => !template.private),
    [templates],
  );

  const templatesToShow = useMemo(
    () =>
      isPrivate ? [...privateTemplates, ...sharedTemplates] : sharedTemplates,
    [isPrivate, privateTemplates, sharedTemplates],
  );

  return (
    <OzoneDrawer
      placement="right"
      closable
      closeIcon={
        <Tooltip
          placement="left"
          title={<FormattedMessage id="esc_to_close" />}
        >
          <Close16 />
        </Tooltip>
      }
      onClose={onClose}
      visible={visible}
      width={688}
      title={<Title>{isPrivate ? newPrivateUpdate : newUpdate}</Title>}
    >
      <Row gutter={[16, 16]}>
        {loading ? (
          <PageLoading />
        ) : templates.length > 0 ? (
          <>
            <Col>
              <CreateFromScratchCard
                onClick={() => createUpdate(templatePrivacy)}
              >
                <StyledTemplateCardIconWrapper
                  data-cy="status_update_from_scratch"
                  type={TemplateObjectType.StatusUpdates}
                >
                  <Add20 />
                </StyledTemplateCardIconWrapper>
                <StyledParagraph ellipsis={{ rows: 2 }}>
                  {isPrivate ? blankPrivateTemplate : blankTemplate}
                </StyledParagraph>
                <Help>{createFromScratch}</Help>
              </CreateFromScratchCard>
            </Col>
            {templatesToShow.map((template) => (
              <Col>
                <TemplateCard
                  onClick={() => onChooseTemplate(template, templatePrivacy)}
                  private={template.private}
                  data-cy={`status_update_template_${template.templateId}`}
                >
                  <TemplateCardIconWrapper
                    type={TemplateObjectType.StatusUpdates}
                  >
                    <IconShell icon={<Bullhorn />} />
                  </TemplateCardIconWrapper>
                  <Paragraph ellipsis={{ rows: 2 }}>
                    {template.templateName}
                  </Paragraph>
                </TemplateCard>
              </Col>
            ))}
          </>
        ) : (
          <StatusUpdatesTemplateEmptyState />
        )}
      </Row>
    </OzoneDrawer>
  );
};
